import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Tag, Popconfirm, Select } from 'antd';
import Card from 'components/card';
import _ from 'lodash';
import { UndoOutlined } from '@ant-design/icons';
import { useZones, Zone } from 'services/ZoneService';
import { DefaultOptionType } from 'antd/es/select';

interface ZoneTagProps {
  zone: Zone;
  removeCallback: (arg?: number) => void;
}

interface ChildrenZoneSelectorProps {
  parentId?: number;
  initialChildrenZones?: (number | undefined)[] | Zone[];
  selectedChildrenZones: Zone[];
  setChildrenZonesIds: React.Dispatch<
    React.SetStateAction<(number | undefined)[]>
  >;
}

function ZoneTag({ zone, removeCallback }: ZoneTagProps) {
  const { id, fullName } = zone;
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Popconfirm
      open={isOpen}
      title="Confirmar eliminación"
      description="¿Seguro que desea remover esta zona?"
      onConfirm={() => {
        removeCallback(id);
        setIsOpen(false);
      }}
      onCancel={() => setIsOpen(false)}
      okText="Yes"
      cancelText="No"
    >
      <Tag
        key={id}
        closable
        style={{ marginBottom: '4px' }}
        onClose={(e) => {
          e.preventDefault();
          setIsOpen(true);
        }}
      >
        {fullName}
      </Tag>
    </Popconfirm>
  );
}

export const ChildrenZonesSelector = ({
  parentId,
  initialChildrenZones,
  selectedChildrenZones,
  setChildrenZonesIds,
}: ChildrenZoneSelectorProps) => {
  const [selectedZoneId, setSelectedZoneId] = useState<number>();
  const {
    zones: { data: zones },
  } = useZones();

  const selectableZoneOptions = useMemo(() => {
    const selectedChildrenZonesIds = selectedChildrenZones.map((z) => z.id);
    return zones
      .filter(
        (z: Zone) =>
          z.id &&
          (!z.parent || z.parent === `/zones/${parentId}`) &&
          !selectedChildrenZonesIds.includes(z.id),
      )
      .map((z: Zone) => ({ value: z.id, label: z.fullName }));
  }, [zones, selectedChildrenZones]);

  const loadChildrenZones = useMemo(
    () => () => {
      let childrenZones;
      if (initialChildrenZones)
        childrenZones = initialChildrenZones
          .map((z: Zone | number | undefined) => {
            if (typeof z === 'number') return z;
            if (z && typeof z === 'object' && 'id' in z) return z.id;
            return undefined;
          })
          .filter((id): id is number => typeof id === 'number');
      setChildrenZonesIds(childrenZones ?? []);
      setSelectedZoneId(undefined);
    },
    [initialChildrenZones, setChildrenZonesIds],
  );

  useEffect(loadChildrenZones, [initialChildrenZones]);

  function addChildrenZone(zoneId: number) {
    setChildrenZonesIds((prev: (number | undefined)[]) => {
      const _zipCodes = [...prev];
      _zipCodes.push(zoneId);
      return _zipCodes.sort((a, b) => (a ?? 0) - (b ?? 0));
    });
    setSelectedZoneId(undefined);
  }

  const removeChildrenZone = (zoneId?: number) => {
    if (!zoneId) return;
    setChildrenZonesIds((prev: (number | undefined)[]) =>
      prev.filter((id) => id !== zoneId),
    );
  };

  return (
    <Card title="Zonas agrupadas" style={{ marginTop: '16px', height: '60%' }}>
      <Space.Compact style={{ width: '100%', marginBottom: '16px' }}>
        <Select
          style={{ width: '99%', overflow: 'hidden' }}
          allowClear
          showSearch
          options={selectableZoneOptions}
          placeholder="Seleccione una zona"
          value={selectedZoneId}
          onSelect={(value) => setSelectedZoneId(value)}
          onClear={() => {
            setSelectedZoneId(undefined);
          }}
          optionFilterProp="label"
          filterSort={(
            optionA: DefaultOptionType,
            optionB: DefaultOptionType,
          ) =>
            (optionA?.label as string)
              .toLowerCase()
              .localeCompare((optionB?.label as string).toLowerCase())
          }
          notFoundContent={null}
        />
        <Button
          disabled={!selectedZoneId}
          type="primary"
          onClick={() => {
            if (selectedZoneId) addChildrenZone(selectedZoneId);
          }}
        >
          Agregar
        </Button>
        {!_.isEqual(initialChildrenZones ?? [], selectedChildrenZones) && (
          <Popconfirm
            title="Confirmar deshacer"
            description="¿Seguro que desea deshacer los cambios?"
            onConfirm={loadChildrenZones}
            okText="Si"
            cancelText="No"
          >
            <Button type="primary" style={{ background: 'orange' }}>
              <UndoOutlined style={{ fontSize: '18' }} />
            </Button>
          </Popconfirm>
        )}
      </Space.Compact>
      {selectedChildrenZones &&
        !!selectedChildrenZones.length &&
        selectedChildrenZones.length > 0 &&
        selectedChildrenZones.map((z: Zone) => (
          <ZoneTag key={z.id} zone={z} removeCallback={removeChildrenZone} />
        ))}
    </Card>
  );
};
